import Image from "next/image";
import Hero from "../components/Hero";
import { HorizontalMarquee } from "../components/HorizontalMarquee";
import NavBar from "../components/NavBar";
import AutoScrollImage from "../components/util";
import { inter, jetbrainsMono } from "@/components/fonts";


const AutoScrollCompanies = () => {
  const companies = [
    { name: "Bosch AI", logo: "/logos/Bosch.webp", url: "https://bosch-ai.com/" },
    { name: "Wuppertal University", logo: "/logos/BergischeUniversitaetWuppertal.webp", url: "https://uni-wuppertal.de/en/" },
    { name: "Lunar Energy", logo: "/logos/Lunar.webp", url: "https://lunarenergy.com" },
    { name: "Lasso GmbH", logo: "/logos/Lasso.webp", url: "https://www.lasso.de/" },
    { name: "Porsche AG", logo: "/logos/Porsche.webp", url: "https://porsche.com" },
    { name: "Complexio", logo: "/logos/complexio.webp", url: "https://complexio.com" },
    { name: "Opel", logo: "/logos/Opel.webp", url: "https://opel.com" },
    { name: "CFEngineering", logo: "/logos/CFE.webp", url: "https://cfengineering.de" },
    { name: "DHBW Ravensburg", logo: "/logos/DHBW.webp", url: "https://ravensburg.dhbw.de/" },
    { name: "RWU University", logo: "/logos/RWU.webp", url: "https://rwu.de" },
    { name: "Liebherr Aerospace", logo: "/logos/LiebherrAerospace.webp", url: "https://liebherr.com/de-de/firmengruppe/standort/lindenberg-profil-3705432" },
  ];

  return (
    <div className="w-full bg-black/[.05] dark:bg-white/[.06] py-12 rounded-lg">
      <HorizontalMarquee>
        {companies.map((company, index) => (
          <a
            key={`${company.name}-${index}`}
            href={company.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center mx-6 group"
          >
            <div className="relative w-32 h-32 bg-gray-100 dark:bg-gray-100 rounded-2xl p-4 flex items-center justify-center shadow-sm transition-all duration-300 group-hover:scale-140 group-hover:shadow-xl group-hover:rotate-1">
              <Image
                src={company.logo}
                alt={`${company.name} logo`}
                fill
                className="object-contain p-2"
                sizes="128px"
              />
            </div>
          </a>
        ))}
      </HorizontalMarquee>
    </div>
  );
};

export default function Home() {
  return (
    <div
      className={`${inter.variable} ${jetbrainsMono.variable} flex flex-col min-h-screen font-[family-name:var(--font-inter)]`}
    >
      {/* Nav Bar */}
      <NavBar />

      {/* Hero Section */}
      <Hero />

      <div className="flex-1 flex flex-col gap-12 sm:gap-16 pt-16 sm:pt-24">
        {/* About Section */}
        <section
          id="about"
          className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12"
        >
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
                About Us
              </h2>
              <p className="text-base sm:text-lg mb-4">
                Welcome to the Tea &apos;n Tech Community! We&apos;re Constantin
                and Manuel. We&apos;ve spent years in the trenches of software
                development, AI, and engineering, and we&apos;re here to share
                what we&apos;ve learned – the good, the bad, and the genuinely
                useful. No corporate fluff, just practical insights and a
                passion for making complex stuff understandable.
              </p>
              <p className="text-base sm:text-lg mb-4">
                We believe that by sharing our real-world experiences, we can
                help you on your own tech journey. Whether you&apos;re debugging
                code at 2 AM or trying to make sense of topology optimization,
                we&apos;ve probably been there too.
              </p>
              <div className="relative my-6 sm:my-8 pl-6 sm:pl-8 border-black/[.1] dark:border-white/[.1]">
                <p className="text-lg sm:text-xl italic text-gray-700 dark:text-gray-300">
                  <span className="text-3xl sm:text-4xl font-serif leading-none text-black/[.2] dark:text-white/[.2] absolute left-0 top-0">
                    &ldquo;
                  </span>
                  The purpose of life is acquiring skill through learning, the
                  meaning of life is serving others with these skills.
                  <span className="text-3xl sm:text-4xl font-serif leading-none text-black/[.2] dark:text-white/[.2] absolute right-0 bottom-0">
                    &rdquo;
                  </span>
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 sm:mt-8">
                <div className="bg-black/[.05] dark:bg-white/[.06] p-4 rounded-lg">
                  <h3 className="font-bold mb-2">Our Approach</h3>
                  <p className="text-sm">
                    Hands-on problem solving with real-world impact
                  </p>
                </div>
                <div className="bg-black/[.05] dark:bg-white/[.06] p-4 rounded-lg">
                  <h3 className="font-bold mb-2">Our Mission</h3>
                  <p className="text-sm">
                    Making complex tech accessible through sharing knowledge
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href="https://www.linkedin.com/in/constantin-diez/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative aspect-square rounded-lg overflow-hidden shadow-lg hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] dark:hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:scale-[1.02] transition-all duration-300 origin-center"
              >
                <Image
                  src="/codie.webp"
                  alt="Constantin Diez"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex items-center gap-2 text-white">
                    <Image
                      src="/linkedin.svg"
                      alt="LinkedIn icon"
                      width={16}
                      height={16}
                      className="invert"
                    />
                    Constantin Diez
                  </div>
                </div>
              </a>
              <a
                href="https://manuel-ramsaier.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative aspect-square rounded-lg overflow-hidden shadow-lg hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] dark:hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:scale-[1.02] transition-all duration-300 origin-center"
              >
                <Image
                  src="/manu.webp"
                  alt="Manuel Ramsaier"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex items-center gap-2 text-white">
                    @ Manuel Ramsaier
                  </div>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* What Drives Us Section */}
        <section className="w-full bg-gradient-to-r from-black/[.08] via-black/[.05] to-black/[.08] dark:from-white/[.08] dark:via-white/[.05] dark:to-white/[.08] py-12 sm:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 dark:from-gray-100 dark:via-gray-300 dark:to-gray-100 bg-clip-text text-transparent">
                What Drives Us
              </h2>
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-0.5 bg-gradient-to-r from-transparent via-black/[.2] to-transparent dark:via-white/[.2]"></div>
            </div>

            <div className="relative flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16 mt-12">
              {/* Main Content Block */}
              <div className="relative">
                <div className="bg-white dark:bg-black/[.4] border border-blue-300 dark:border-blue-600 rounded-lg p-6 lg:p-8 max-w-md">
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    Tea & Tech
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    Sharing real-world experiences and practical insights from
                    our journey through AI, software, and engineering.
                  </p>
                </div>

                {/* Animated Connecting Lines - positioned relative to main box */}
                <div className="hidden lg:block">
                  {/* Line to Practical box */}
                  <div className="absolute top-1/2 -right-16 w-16 h-1 -translate-y-12">
                    <svg className="w-full h-full" viewBox="0 0 64 4">
                      <line
                        x1="0"
                        y1="2"
                        x2="64"
                        y2="2"
                        stroke="rgb(147 197 253)"
                        strokeWidth="2"
                        strokeDasharray="4 4"
                        strokeLinecap="round"
                        className="opacity-50 dark:stroke-blue-600"
                      />
                      <line
                        x1="0"
                        y1="2"
                        x2="64"
                        y2="2"
                        stroke="white"
                        strokeWidth="2"
                        strokeDasharray="12 52"
                        strokeDashoffset="0"
                        strokeLinecap="round"
                        className="flowing-stroke opacity-80"
                      />
                    </svg>
                  </div>

                  {/* Line to Accessible box */}
                  <div className="absolute top-1/2 -right-16 w-16 h-1 translate-y-12">
                    <svg className="w-full h-full" viewBox="0 0 64 4">
                      <line
                        x1="0"
                        y1="2"
                        x2="64"
                        y2="2"
                        stroke="rgb(147 197 253)"
                        strokeWidth="2"
                        strokeDasharray="4 4"
                        strokeLinecap="round"
                        className="opacity-50 dark:stroke-blue-600"
                      />
                      <line
                        x1="0"
                        y1="2"
                        x2="64"
                        y2="2"
                        stroke="white"
                        strokeWidth="2"
                        strokeDasharray="12 52"
                        strokeDashoffset="0"
                        strokeLinecap="round"
                        className="flowing-stroke-delayed opacity-80"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Feature Blocks */}
              <div className="space-y-6">
                <div className="bg-white dark:bg-black/[.4] border border-blue-300 dark:border-blue-600 rounded-lg p-4 lg:p-6 max-w-sm">
                  <h4 className="text-lg lg:text-xl font-bold text-white dark:text-white mb-2">
                    Practical
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300 text-sm lg:text-base">
                    Hands-on problem solving with real-world impact and genuine
                    learning experiences.
                  </p>
                </div>

                <div className="bg-white dark:bg-black/[.4] border border-blue-300 dark:border-blue-600 rounded-lg p-4 lg:p-6 max-w-sm">
                  <h4 className="text-lg lg:text-xl font-bold text-white dark:text-white mb-2">
                    Accessible
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300 text-sm lg:text-base">
                    Making complex tech understandable through clear
                    explanations and shared knowledge.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Custom CSS for animations */}
          <style jsx>{`
            .flowing-stroke {
              animation: flowStroke 3s linear infinite;
            }

            .flowing-stroke-delayed {
              animation: flowStroke 3s linear infinite;
              animation-delay: 1.5s;
            }

            @keyframes flowStroke {
              0% {
                stroke-dashoffset: 64;
              }
              100% {
                stroke-dashoffset: -64;
              }
            }
          `}</style>
        </section>

        {/* Projects Section */}
        <section
          id="projects"
          className="w-full bg-black/[.05] dark:bg-white/[.06] py-12 sm:py-20"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-center">
              Things We&apos;ve Built
            </h2>
            <div className="max-w-2xl mx-auto text-center mb-8 sm:mb-12">
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300">
                Here are a couple of projects we&apos;re particularly proud of.
                They solve real problems and, more importantly, people actually
                use them.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              <a
                href="https://snipify.io"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white dark:bg-black/[.4] rounded-xl overflow-hidden shadow-lg hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] dark:hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:scale-[1.02] transition-all duration-300 origin-center"
              >
                <AutoScrollImage
                  src="/podcast-ripper-full.webp"
                  alt="Snipify Website"
                  crop="podcast"
                />
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold mb-2">
                    Snipify
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-4">
                    An AI-powered tool that automatically extracts interesting
                    video clips from podcasts and allows you to share them with
                    your friends on social media.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-black/[.05] dark:bg-white/[.06] rounded-full text-sm">
                      Audio Processing
                    </span>
                    <span className="px-3 py-1 bg-black/[.05] dark:bg-white/[.06] rounded-full text-sm">
                      YouTube API
                    </span>
                    <span className="px-3 py-1 bg-black/[.05] dark:bg-white/[.06] rounded-full text-sm">
                      Automation
                    </span>
                  </div>
                </div>
              </a>

              <a
                href="https://circle-of-cultivation.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white dark:bg-black/[.4] rounded-xl overflow-hidden shadow-lg hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] dark:hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:scale-[1.02] transition-all duration-300 origin-center"
              >
                <AutoScrollImage
                  src="/coc-website-full.webp"
                  alt="Circle of Cultivation"
                  crop="center"
                  speed={30}
                />
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold mb-2">
                    Circle of Cultivation
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-4">
                    A video streaming platform dedicated to online courses for
                    self-improvement. We combine traditional teaching methods
                    with modern technology to deliver high-quality learning
                    experiences.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-black/[.05] dark:bg-white/[.06] rounded-full text-sm">
                      Education
                    </span>
                    <span className="px-3 py-1 bg-black/[.05] dark:bg-white/[.06] rounded-full text-sm">
                      Community
                    </span>
                    <span className="px-3 py-1 bg-black/[.05] dark:bg-white/[.06] rounded-full text-sm">
                      Innovation
                    </span>
                  </div>
                </div>
              </a>

              <a
                href="https://sunsetwriter.tea-n-tech.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white dark:bg-black/[.4] rounded-xl overflow-hidden shadow-lg hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] dark:hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:scale-[1.02] transition-all duration-300 origin-center"
              >
                <AutoScrollImage
                  src="https://assets.tea-n-tech.com/2025-07-13-sunset-writer-project.png"
                  alt="Sunset Writer - Zen Writing App"
                  crop="center"
                  speed={30}
                />
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold mb-2">
                    Sunset Writer
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-4">
                    A minimalist, zen-inspired writing app designed for peaceful journaling and mindful reflection. 
                    Strip away distractions and focus on what matters - your thoughts, your words, your inner voice.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-black/[.05] dark:bg-white/[.06] rounded-full text-sm">
                      Mindfulness
                    </span>
                    <span className="px-3 py-1 bg-black/[.05] dark:bg-white/[.06] rounded-full text-sm">
                      Journaling
                    </span>
                    <span className="px-3 py-1 bg-black/[.05] dark:bg-white/[.06] rounded-full text-sm">
                      Zen Design
                    </span>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Experience Section */}
        <section
          id="experience"
          className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12"
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">
            Our Journey
          </h2>
          <div className="max-w-4xl mx-auto text-center mb-8 sm:mb-12">
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300">
              We&apos;re not about pretending to have all the answers, but about
              exploring questions together and offering practical perspectives
              from our time in the field. Here&apos;s what we&apos;ve been up
              to:
            </p>
          </div>

          {/* Auto-scrolling companies */}
          <div className="mb-8 sm:mb-12">
            <AutoScrollCompanies />
          </div>

          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 mb-12">
            {/* Constantin's Experience */}
            <div className="bg-black/[.05] dark:bg-white/[.06] p-6 sm:p-8 rounded-xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden">
                  <Image
                    src="/codie.webp"
                    alt="Constantin Diez"
                    width={64}
                    height={64}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Constantin</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    AI & Software Engineer
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                {/* <p className="text-sm sm:text-base">
                  <strong>Right now,</strong> I&apos;m a Senior Software
                  Engineer at Complexio, where my team and I build AI agents to
                  help people cut through bureaucracy. This means a lot of
                  Python, working with large language models, and figuring out
                  how to make AI genuinely useful in daily workflows.
                </p> */}
                <p className="text-sm sm:text-base">
                  <strong>My background:</strong> I&apos;ve managed engineering
                  teams building virtual power plants at Lunar Energy, developed
                  cloud systems controlling thousands of home batteries, led AI
                  platform development at Bosch Center for AI, and did a PhD
                  wrestling with massive crash simulation datasets using machine
                  learning.
                </p>
                <p className="text-sm sm:text-base">
                  <strong>Knowledge sharing</strong> is a big deal for me –
                  whether it&apos;s mentoring colleagues or running our Tea
                  &apos;n Tech community. I find that the more we empower each
                  other, the faster we all learn and build cool things.
                </p>
                <p className="text-sm sm:text-base">
                  <strong>Open source passion:</strong> I created{" "}
                  <span className="font-mono text-sm bg-black/[.1] dark:bg-white/[.1] px-2 py-1 rounded">
                    lasso-python
                  </span>
                  to help engineers work with complex simulation data –
                  it&apos;s now used in several large automotive companies.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="px-3 py-1 bg-white dark:bg-black/[.4] rounded-full text-xs">
                    Python
                  </span>
                  <span className="px-3 py-1 bg-white dark:bg-black/[.4] rounded-full text-xs">
                    AI/ML
                  </span>
                  <span className="px-3 py-1 bg-white dark:bg-black/[.4] rounded-full text-xs">
                    Cloud Tech
                  </span>
                  <span className="px-3 py-1 bg-white dark:bg-black/[.4] rounded-full text-xs">
                    React/TypeScript
                  </span>
                </div>
              </div>
            </div>

            {/* Manuel's Experience */}
            <div className="bg-black/[.05] dark:bg-white/[.06] p-6 sm:p-8 rounded-xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden">
                  <Image
                    src="/manu.webp"
                    alt="Manuel Ramsaier"
                    width={64}
                    height={64}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Manuel</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Dr.-Ing. & Simulation Expert
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                {/* <p className="text-sm sm:text-base">
                  <strong>Currently,</strong> I&apos;m a Dr.-Ing. and Head of
                  Analysis and Simulation at CFEngineering GmbH, where I dive
                  deep into making structures lighter and stronger using
                  advanced techniques like topology optimization and the Finite
                  Element Method (FEM).
                </p> */}
                <p className="text-sm sm:text-base">
                  <strong>Hands-on approach:</strong> You&apos;ll find me
                  working with Software, CAE tools, simulation software, and
                  exploring how programming (especially Python) can push
                  engineering boundaries. My YouTube channel is packed with
                  practical tutorials.
                </p>
                <p className="text-sm sm:text-base">
                  <strong>My research focus:</strong> I work on integrating
                  simulation tools into the design process and automate and improve how we
                  engineer things. It&apos;s about making powerful tools
                  actually usable.
                </p>
                <p className="text-sm sm:text-base">
                  <strong>Teaching is key for me</strong> – I lecture at
                  universities like Hochschule Ravensburg-Weingarten and create
                  YouTube tutorials to make complex engineering concepts more
                  accessible. There&apos;s nothing quite like that
                  &quot;aha!&quot; moment when someone gets it.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="px-3 py-1 bg-white dark:bg-black/[.4] rounded-full text-xs">
                    Topology Optimization
                  </span>
                  <span className="px-3 py-1 bg-white dark:bg-black/[.4] rounded-full text-xs">
                    FEM
                  </span>
                  <span className="px-3 py-1 bg-white dark:bg-black/[.4] rounded-full text-xs">
                    CAE
                  </span>
                  <span className="px-3 py-1 bg-white dark:bg-black/[.4] rounded-full text-xs">
                    Python
                  </span>
                  <span className="px-3 py-1 bg-white dark:bg-black/[.4] rounded-full text-xs">
                    Optimization
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section
          id="contact"
          className="w-full bg-black/[.05] dark:bg-white/[.06] py-12 sm:py-20"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
                Let&apos;s Chat
              </h2>
              <p className="text-base sm:text-lg mb-6 sm:mb-8">
                Got a question about AI, engineering, or just want to talk tech?
                We&apos;re always up for a good conversation. Whether
                you&apos;re stuck on a problem, curious about our projects, or
                just want to share what you&apos;re working on – drop us a line!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <a
                  href="mailto:contact@tea-n-tech.com"
                  className="bg-black text-white dark:bg-white dark:text-black px-6 sm:px-8 py-3 sm:py-4 rounded-full font-medium hover:opacity-90 transition-opacity text-center"
                >
                  Send us an Email
                </a>
                <a
                  href="https://twitter.com/tea_n_tech"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-black dark:border-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-medium hover:bg-black/[.05] dark:hover:bg-white/[.06] transition-colors flex items-center justify-center gap-2"
                >
                  Follow us on
                  <Image
                    src="/twitter.svg"
                    alt="Twitter icon"
                    width={20}
                    height={20}
                    className="dark:invert"
                  />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="w-full bg-black/[.05] dark:bg-white/[.06] py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
              {/* Quick Links */}
              <div>
                <h3 className="font-bold text-lg mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li>
                    <a
                      href="#about"
                      className="hover:underline hover:underline-offset-4"
                    >
                      About Us
                    </a>
                  </li>
                  <li>
                    <a
                      href="#projects"
                      className="hover:underline hover:underline-offset-4"
                    >
                      Projects
                    </a>
                  </li>
                  <li>
                    <a
                      href="#experience"
                      className="hover:underline hover:underline-offset-4"
                    >
                      Experience
                    </a>
                  </li>
                  <li>
                    <a
                      href="#contact"
                      className="hover:underline hover:underline-offset-4"
                    >
                      Contact
                    </a>
                  </li>
                </ul>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="font-bold text-lg mb-4">Connect With Us</h3>
                <ul className="space-y-2">
                  <li>
                    <a
                      href="https://x.com/tea_n_tech"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline hover:underline-offset-4 flex items-center gap-2"
                    >
                      <Image
                        src="/twitter.svg"
                        alt="Twitter icon"
                        width={16}
                        height={16}
                        className="dark:invert"
                      />
                      Twitter
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.youtube.com/@teantech"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline hover:underline-offset-4 flex items-center gap-2"
                    >
                      <Image
                        src="/youtube.svg"
                        alt="YouTube icon"
                        width={16}
                        height={16}
                        className="dark:invert"
                      />
                      YouTube
                    </a>
                  </li>
                </ul>
              </div>

              {/* Team */}
              <div>
                <h3 className="font-bold text-lg mb-4">Our Team</h3>
                <ul className="space-y-2">
                  <li>
                    <a
                      href="https://www.linkedin.com/in/constantin-diez/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline hover:underline-offset-4 flex items-center gap-2"
                    >
                      <Image
                        src="/linkedin.svg"
                        alt="LinkedIn icon"
                        width={16}
                        height={16}
                        className="dark:invert"
                      />
                      Constantin Diez
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://manuel-ramsaier.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline hover:underline-offset-4 flex items-center gap-2"
                    >
                      @ Manuel Ramsaier
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-black/[.1] dark:border-white/[.1] text-center text-sm text-gray-600 dark:text-gray-400">
              © {new Date().getFullYear()} Tea & Tech. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
